import { configureStore } from '@reduxjs/toolkit';
import { combineReducers } from 'redux';
import {
  persistReducer,
  persistStore,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';

// Features
import exploreReducer from 'features/explore/exploreSlice';
import sessionReducer from 'features/session/sessionSlice';
import userReducer from 'features/user/userSlice';
import backofficeReducer from 'features/backoffice/backofficeSlice';

// Services
import { exploreApi } from 'services/exploreApi';
import {
  unauthenticatedMiddleware,
  serverErrorMiddleware,
  rtkQueryErrorLogger,
} from './middlewares/middleware';

const sessionPersistConfig = {
  key: 'session',
  storage: storage,
};

const userPersistConfig = {
  key: 'user',
  storage: storage,
};

const backofficePersistConfig = {
  key: 'backoffice',
  storage: storage,
};

const rootReducer = combineReducers({
  session: persistReducer(sessionPersistConfig, sessionReducer),
  user: persistReducer(userPersistConfig, userReducer),
  backoffice: persistReducer(backofficePersistConfig, backofficeReducer),
  explore: exploreReducer,
  [exploreApi.reducerPath]: exploreApi.reducer,
});

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(
      exploreApi.middleware,
      unauthenticatedMiddleware,
      serverErrorMiddleware,
      rtkQueryErrorLogger
    ),
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;

// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;

export const persistor = persistStore(store);
